/**
 * Global Error Handler for API responses
 */

import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { ApiError } from '../types/api.types';

/**
 * Handles API errors and converts them to a standardized format
 */
export const handleApiError = (error: unknown): ApiError => {
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const statusCode = error.response?.status;
    const errors = error.response?.data?.errors;

    // Log to console in development
    if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
      console.error('API Error:', { message, statusCode, errors });
    }

    return {
      message,
      statusCode,
      errors,
    };
  }

  if (error instanceof Error) {
    if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
      console.error('Error:', error.message);
    }
    
    return {
      message: error.message,
    };
  }

  const message = 'An unexpected error occurred';
  if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
    console.error('Unknown Error:', error);
  }

  return {
    message,
  };
};

/**
 * Shows error notification to user
 */
export const showErrorNotification = (error: ApiError | string): void => {
  const message = typeof error === 'string' ? error : error.message;
  
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  // Log to console in development
  if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
    console.error('Error Notification:', error);
  }
};

/**
 * Shows success notification to user
 */
export const showSuccessNotification = (message: string): void => {
  toast.success(message, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  // Log to console in development
  if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
    // eslint-disable-next-line no-console
    console.log('Success Notification:', message);
  }
};

/**
 * Shows info notification to user
 */
export const showInfoNotification = (message: string): void => {
  toast.info(message, {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Shows warning notification to user
 */
export const showWarningNotification = (message: string): void => {
  toast.warning(message, {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Logs error to error tracking service (e.g., Sentry)
 */
export const logErrorToService = (error: ApiError, context?: Record<string, unknown>): void => {
  // In development, just log to console
  if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
    console.error('Error logged to service:', error, context);
  }
  
  // TODO: Integrate with error tracking service in production
  // if (process.env.NODE_ENV === 'production') {
  //   Sentry.captureException(error, { extra: context });
  // }
};

